import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { GeminiService } from './gemini.service';

export interface NarrativeContext {
  centreName: string;
  period: string;
  donorName: string;
}

export interface CounsellingContext extends NarrativeContext {
  individual: number;
  group: number;
  assessment: number;
  total: number;
  /** Highlights from the Counselling team's own impact reports for the year, if fetched. */
  departmentHighlights?: string;
}

export interface TeachingContext extends NarrativeContext {
  individual: number;
  group: number;
  total: number;
  /** Highlights from the Education team's own impact reports for the year, if fetched. */
  departmentHighlights?: string;
}

/** Extra prompt lines grounding a narrative in the department's own reported highlights. */
function departmentBlock(team: string, highlights: string | undefined): string {
  const text = (highlights || '').trim();
  return text
    ? `\nWhat the ${team} team itself reported this year (build the narrative on these specifics; do not contradict them or invent other events):\n${text}\n`
    : '';
}

export interface CaseStudyInput {
  name: string;
  gender: string;
  age: number;
  city: string;
  state: string;
  diagnosis: string;
  hospital: string;
  stayDuration: string;
  educationHighlights: string;
  counsellingHighlights: string;
}

export interface CaseStudyAiOutput {
  openingQuote: string;
  paragraphs: string[];
  teachingBullets: string[];
  counsellingBullets: string[];
  closingQuote: string;
}

export interface VolunteerEventAiOutput {
  outcome: string;
  impact: string;
  conclusion: string;
}

export interface ReportHtmlPayload {
  centreName: string;
  period: string;
  donorName: string;
  cumulativeStats: any;
  admissionStats: any;
  educationStats: any;
  counsellingStats: any;
  nutritionNarrative: string;
  transportNarrative: string;
  counsellingNarrative: string;
  teachingNarrative: string;
  counsellingPhotos: string[];
  teachingPhotos: string[];
  caseStudies: any[];
  volunteerEvents: any[];
}

@Injectable({ providedIn: 'root' })
export class AnnualImpactReportAiService {

  constructor(private gemini: GeminiService) {}

  async generateNutritionNarrative(ctx: NarrativeContext): Promise<string> {
    const prompt = `You are writing content for an annual donor impact report for Access Life, an NGO that supports children undergoing cancer treatment and their families by providing shelter, nutrition, education, counselling, and transport.

Write 3 paragraphs (220–280 words total) describing the nutrition services at the Access Life ${ctx.centreName} centre during ${ctx.period}, supported by ${ctx.donorName}.

Cover:
- Balanced, immunity-boosting meals tailored to each child's treatment phase (chemotherapy, post-BMT recovery, immunocompromised states)
- Strict hygiene protocols for medically vulnerable children
- Home-style, culturally familiar meals that provide emotional comfort and belonging
- Mealtimes as community moments that build bonds between families

Tone: warm, professional, evidence-based. Output plain text paragraphs separated by a blank line. No headings, no bullet points, no markdown.`;

    return firstValueFrom(this.gemini.generateContent(prompt));
  }

  async generateTransportNarrative(ctx: NarrativeContext): Promise<string> {
    const prompt = `You are writing content for an annual donor impact report for Access Life, an NGO that supports children undergoing cancer treatment.

Write 3 paragraphs (220–280 words total) describing transport and proximity support at the Access Life ${ctx.centreName} centre during ${ctx.period}, supported by ${ctx.donorName}.

Cover:
- The centre's proximity to the treatment hospital eliminating daily travel burden
- Children attending every appointment and chemotherapy session without fatigue or delays
- Access Life coordinating comfortable, timely transport for inter-hospital investigations
- How ${ctx.donorName}'s support sustains these transport lifelines and ensures continuity of care

Tone: warm, professional. Output plain text paragraphs separated by a blank line. No headings, no bullet points, no markdown.`;

    return firstValueFrom(this.gemini.generateContent(prompt));
  }

  async generateCounsellingNarrative(ctx: CounsellingContext): Promise<string> {
    const prompt = `You are writing content for an annual donor impact report for Access Life, an NGO that supports children undergoing cancer treatment.

Write 4 paragraphs (300–380 words total) describing counselling services at the Access Life ${ctx.centreName} centre during ${ctx.period}.
Session counts: ${ctx.individual} individual sessions, ${ctx.group} group sessions, ${ctx.assessment} assessments — ${ctx.total} total.
${departmentBlock('Counselling', ctx.departmentHighlights)}
Cover:
- Emotional challenges families face: anxiety, fear, sleep disturbances, suicidal ideation in caregivers, low self-esteem in children
- Group counselling topics: psychoeducation on emotional regulation, "Good Touch and Bad Touch" safety sessions, feeding style guidance for caregivers
- Individual session approaches: Cognitive Behavioural Therapy (CBT), narrative therapy, grief processing
- Measurable outcomes: improved parent-child communication, resilience, practical coping tools

Tone: warm, professional, evidence-based. Output plain text paragraphs separated by a blank line. No headings, no bullet points, no markdown.`;

    return firstValueFrom(this.gemini.generateContent(prompt));
  }

  async generateTeachingNarrative(ctx: TeachingContext): Promise<string> {
    const prompt = `You are writing content for an annual donor impact report for Access Life, an NGO that supports children undergoing cancer treatment.

Write 4 paragraphs (300–380 words total) describing education and teaching services at the Access Life ${ctx.centreName} centre during ${ctx.period}.
Session counts: ${ctx.individual} individual sessions, ${ctx.group} group sessions — ${ctx.total} total.
${departmentBlock('Education', ctx.departmentHighlights)}
Cover:
- Education as keeping curiosity alive during treatment, not keeping up with curriculum
- Themed learning celebrations: National Science Day experiments, Arts and Theatre days, nature-based activities
- Group sessions creating an inclusive environment across academic levels and health states
- How creative learning gives children a way to express emotions beyond their illness

End with a short paragraph that begins exactly with "Overall Impact:" followed by 5 bullet points on separate lines each starting with "•".

Tone: warm, inspiring, professional. Output plain text paragraphs separated by a blank line. No markdown except the bullet points as described.`;

    return firstValueFrom(this.gemini.generateContent(prompt));
  }

  async generateCaseStudy(input: CaseStudyInput): Promise<CaseStudyAiOutput> {
    const prompt = `Generate a case study for an Access Life NGO annual donor impact report. Access Life provides shelter, nutrition, education, counselling, and transport to children undergoing cancer treatment and their families.

Child details:
- Name: ${input.name}
- Gender: ${input.gender}
- Age: ${input.age} years
- City/State: ${input.city}, ${input.state}
- Diagnosis: ${input.diagnosis}
- Hospital: ${input.hospital}
- Stay duration at Access Life: ${input.stayDuration}
- Education context: ${input.educationHighlights || 'actively engaged in learning activities throughout the stay'}
- Counselling context: ${input.counsellingHighlights || 'showed meaningful emotional growth through counselling support'}

Respond ONLY with valid JSON matching this exact structure:
{
  "openingQuote": "An inspiring quote about courage or hope, 12–18 words, not attributed to anyone",
  "paragraphs": [
    "Para 1 (70–90 words): Introduce ${input.name}, family background, diagnosis, and journey to ${input.hospital}",
    "Para 2 (70–90 words): Challenges faced — financial strain, emotional toll, being far from home without support",
    "Para 3 (70–90 words): How Access Life became their anchor — shelter, nutrition, counselling, education, transport",
    "Para 4 (70–90 words): ${input.name}'s education journey — incorporate the education context provided above",
    "Para 5 (60–80 words): Happy discharge, renewed hope, and ${input.name}'s outlook for the future"
  ],
  "teachingBullets": [
    "6 specific bullet points about ${input.name}'s education highlights, interests, and academic progress — draw from the education context"
  ],
  "counsellingBullets": [
    "7 specific bullet points about ${input.name}'s counselling journey — initial presentation, therapeutic approach, and measurable emotional growth — draw from the counselling context"
  ],
  "closingQuote": "A hopeful closing quote about recovery or the future, 12–18 words, not attributed to anyone"
}

Use ${input.name}'s actual name throughout. Make bullets specific to this child, not generic. Paragraphs must be complete prose sentences.`;

    return firstValueFrom(this.gemini.generateJSON<CaseStudyAiOutput>(prompt));
  }

  async generateVolunteerEventSummary(
    event: { title: string; date: string; donorName: string; activities: { heading: string; description: string }[] }
  ): Promise<VolunteerEventAiOutput> {
    const activitiesList = event.activities
      .filter(a => a.heading)
      .map((a, i) => `${i + 1}. ${a.heading}: ${a.description}`)
      .join('\n');

    const prompt = `Write a volunteer event summary for an Access Life NGO annual impact report. Access Life supports children undergoing cancer treatment.

Event: ${event.title}
Date: ${event.date}
Volunteer organisation: ${event.donorName}
Activities conducted:
${activitiesList}

Respond ONLY with valid JSON matching this exact structure:
{
  "outcome": "2–3 sentences (60–80 words): How the event unfolded, the atmosphere, volunteer enthusiasm, and overall success",
  "impact": "3–4 sentences (80–110 words): The emotional and psychological impact on children and caregivers — joy, restored confidence, sense of dignity and belonging, caregivers feeling less alone",
  "conclusion": "2–3 sentences (50–70 words): Gratitude to ${event.donorName} for their compassionate investment and anticipation of continued partnership"
}

Tone: warm, grateful, professional. Reference the specific activities listed above. Do not use generic filler.`;

    return firstValueFrom(this.gemini.generateJSON<VolunteerEventAiOutput>(prompt));
  }
}